import React, { Component } from "react"
import Card from "../components/Card"
import alertService from "../services/alert.service"
import subscriptionApi from "../apis/subscriptionApi"
import {useUserPlan} from "../utils/userPlan.util";
import ContentLayout from "../components/ContentLayout";
import ContentMain from "../components/ContentMain";
import HeadingRow from "../components/HeadingRow";

interface Props {}

interface State {
    apiKey: string
    apiSecret: string
    printNodeApiKey: string
    hasPrintNodeApiKey: boolean
    hasApiKey: boolean
    isLoading: boolean
    isSavingPrintNodeApiKey: boolean
    isAdvanced: boolean
}


class ApiCredentials extends Component<Props, State> {
    state: State = {
        apiKey: "",
        apiSecret: "",
        printNodeApiKey: "",
        hasPrintNodeApiKey: false,
        hasApiKey: false,
        isLoading: false,
        isSavingPrintNodeApiKey: false,
        isAdvanced: false
    }

    componentDidMount() {
        this.fetchMembershipData()
    }

    fetchMembershipData () {
        const {isAdvanced} = useUserPlan();

        if (isAdvanced) {
            this.setState({ isAdvanced: true });

            subscriptionApi.getApiClient((client) => {
                if (!client?.api_key) return
                this.setState({
                    apiKey: client.api_key,
                    printNodeApiKey: client.print_node_api_key || "",
                    hasPrintNodeApiKey: !!client.has_print_node_api_key,
                    hasApiKey: true
                });
            });
        }
    }

    handleGenerateApiKey = () => {
        this.setState({ isLoading: true })

        subscriptionApi.generateApiClientSecret((res) => {
            this.setState({ isLoading: false })

            if (!res) {
                alertService.warning("Failed to generate API credentials")
                return
            }

            this.setState({
                apiKey: res.api_key,
                apiSecret: res.api_secret,
                hasApiKey: true
            })

            alertService.success("API Key generated successfully. Secret is shown only once.")
        })
    }
    handleRegenerateSecret = () => {
        this.setState({ isLoading: true })

        subscriptionApi.regenerateApiClientSecret((res) => {
            this.setState({ isLoading: false })

            if (!res) {
                alertService.warning("Failed to regenerate secret")
                return
            }

            this.setState({
                apiKey: res.api_key,
                apiSecret: res.api_secret,
                hasApiKey: true
            })

            alertService.success("Secret regenerated successfully. Copy it now.")
        })
    }

    handlePrintNodeApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ printNodeApiKey: event.target.value })
    }

    handleSavePrintNodeApiKey = () => {
        const printNodeApiKey = this.state.printNodeApiKey.trim()

        if (!this.state.hasApiKey) {
            alertService.warning("Generate API credentials before saving a PrintNode API key.")
            return
        }

        if (!printNodeApiKey) {
            alertService.warning("PrintNode API key is required.")
            return
        }

        this.setState({ isSavingPrintNodeApiKey: true })

        subscriptionApi.updatePrintNodeApiKey(printNodeApiKey, (res) => {
            this.setState({ isSavingPrintNodeApiKey: false })

            if (!res) {
                alertService.warning("Failed to save PrintNode API key")
                return
            }

            this.setState({
                printNodeApiKey: res.print_node_api_key || printNodeApiKey,
                hasPrintNodeApiKey: res.has_print_node_api_key
            })

            alertService.success("PrintNode API key saved successfully.")
        })
    }

    handleDeletePrintNodeApiKey = () => {
        if (!this.state.hasPrintNodeApiKey) return

        this.setState({ isSavingPrintNodeApiKey: true })

        subscriptionApi.deletePrintNodeApiKey((res) => {
            this.setState({ isSavingPrintNodeApiKey: false })

            if (!res) {
                alertService.warning("Failed to delete PrintNode API key")
                return
            }

            this.setState({
                printNodeApiKey: "",
                hasPrintNodeApiKey: false
            })

            alertService.success("PrintNode API key deleted successfully.")
        })
    }

    copyToClipboard = (text: string) => {
        if (!text) return

        navigator.clipboard
            .writeText(text)
            .then(() => alertService.success("Copied successfully"))
            .catch(() => alertService.warning("Failed to copy"))
    }


    render () {
        const {isFree, isAdvanced} = useUserPlan();
        console.log("hasApiKey === ",this.state.hasApiKey);

        return (
            <ContentLayout>
                <ContentMain layout="twoThird">
                    <Card>
                        <HeadingRow
                            heading="Api Credentials"
                            doc='categories'>
                        </HeadingRow>
                        {isAdvanced && (
                            <div className="max-w-md border border-gray-300 border-solid bg-white p-5 mt-5 shadow-sm">
                                <h3 className="mb-2 text-base font-medium text-gray-900">
                                    API Credentials
                                </h3>

                                <p className="text-sm text-gray-600 mb-3">
                                    Use these credentials to integrate label printing into your external system.
                                </p>

                                {!this.state.hasApiKey && (
                                    <button
                                        onClick={this.handleGenerateApiKey}
                                        className="bg-blue-600 text-white px-4 py-2 text-sm"
                                    >
                                        Generate API Key & Secret
                                    </button>
                                )}

                                {this.state.hasApiKey && (
                                    <div className="mt-4 space-y-4 text-sm">
                                        <div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500">API Key:</span>
                                                <button
                                                    className="text-blue-600 text-xs"
                                                    onClick={() => this.copyToClipboard(this.state.apiKey)}
                                                >
                                                    Copy
                                                </button>
                                            </div>

                                            <div className="font-medium text-gray-800 break-all">
                                                {this.state.apiKey}
                                            </div>
                                        </div>

                                        {this.state.apiSecret && (
                                            <div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-500">API Secret:</span>
                                                    <button
                                                        className="text-blue-600 text-xs"
                                                        onClick={() => this.copyToClipboard(this.state.apiSecret)}
                                                    >
                                                        Copy
                                                    </button>
                                                </div>

                                                <div className="font-medium text-gray-800 break-all">
                                                    {this.state.apiSecret}
                                                </div>

                                                <p className="text-red-500 text-xs mt-2">
                                                    Warning: Secret will be shown only once. Copy and store it securely.
                                                </p>
                                            </div>
                                        )}

                                        <button
                                            onClick={this.handleRegenerateSecret}
                                            className="bg-gray-900 text-white px-4 py-2 text-sm"
                                        >
                                            Regenerate Secret
                                        </button>

                                        <div className="pt-4 border-t border-gray-200">
                                            <h4 className="mb-2 text-sm font-medium text-gray-900">
                                                PrintNode API Key
                                            </h4>

                                            <p className="text-xs text-gray-600 mb-3">
                                                Required for PrintNode ZPL and PrintNode PDF API printing.
                                            </p>

                                            {this.state.hasPrintNodeApiKey && (
                                                <p className="text-xs text-green-700 mb-3">
                                                    PrintNode API key is saved. Enter a new key to replace it.
                                                </p>
                                            )}

                                            <input
                                                className="input-default"
                                                value={this.state.printNodeApiKey}
                                                placeholder="PrintNode API Key"
                                                onChange={this.handlePrintNodeApiKeyChange}
                                            />

                                            <button
                                                onClick={this.handleSavePrintNodeApiKey}
                                                className="bg-blue-600 text-white px-4 py-2 text-sm mt-3"
                                                disabled={this.state.isSavingPrintNodeApiKey}
                                            >
                                                Save PrintNode API Key
                                            </button>

                                            {this.state.hasPrintNodeApiKey && (
                                                <button
                                                    onClick={this.handleDeletePrintNodeApiKey}
                                                    className="bg-red-600 text-white px-4 py-2 text-sm mt-3 ml-2"
                                                    disabled={this.state.isSavingPrintNodeApiKey}
                                                >
                                                    Delete PrintNode API Key
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* ── Security notice ── */}
                        <div className="max-w-md mt-6 bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900 space-y-2">
                            <p className="font-semibold flex items-center gap-1">
                                🔐 Keep your credentials safe
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-amber-800">
                                <li>Safeguard your API key, token, and secret. <strong>Never share them.</strong></li>
                                <li>Never expose credentials in frontend or client-side code.</li>
                                <li><strong>Best practice:</strong> Proxy all API calls through your own backend so credentials stay server-side only.</li>
                            </ul>
                        </div>
                    </Card>
                </ContentMain>
            </ContentLayout>
        )
    }
}

export default ApiCredentials
